<template>
  <div class="page">
    <!-- 1) Mapa -->
    <div class="map-container">
      <div class="map-wrapper">
        <l-map 
          :zoom="zoomMapa" 
          :center="mapCenter" 
          :use-global-leaflet="false" 
          ref="lmap"
          :options="{ attributionControl: false, minZoom: minZoom, maxZoom: maxZoom, maxBounds: spainBounds, maxBoundsViscosity: 1.0 }">
          <l-tile-layer 
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" 
            layer-type="base"
            name="OpenStreetMap">
          </l-tile-layer>
          <!-- Marcador de ubicación del usuario -->
          <l-marker 
            v-if="userLocation"
            :lat-lng="[userLocation.lat, userLocation.lon]"
            :icon="userLocationIcon">
            <l-popup>{{ $t("components.chargerresultsmapview.tu_ubicacion") }}</l-popup>
          </l-marker>
          
          <!-- Los marcadores se agregan programáticamente al mapa -->
        </l-map>
      </div>
    </div>

    <!-- Mensaje si no hay acceso a la ubicación -->
    <div v-if="locationError" class="message-container">
      <div class="location-icon"><img src="@assets/images/location-dot.svg" alt="Location" /></div>
      <div class="message-text">{{ locationError }}</div>
    </div>

    <!-- Panel flotante con ChargerListItem -->
    <div v-if="selectedPoint" class="floating-panel">
      <button class="close-button" @click="closeModal">✕</button>
      <ChargerListItem 
        :name="selectedPointData.name" 
        :distanceKm="selectedPointData.distanceKm"
        :priceKwh="selectedPointData.priceKwh" 
        :totalCost="selectedPointData.totalCost"
        :powerKw="selectedPointData.powerKw" 
        :chargeTimeMin="selectedPointData.chargeTimeMin"
        :latitud="selectedPointData.latitud" 
        :longitud="selectedPointData.longitud" 
        :Libre="selectedPointData.libre" 
        :Total="selectedPointData.total"
        :groupedCount="selectedChargerGroup.length"
        :currentIndex="currentChargerIndex"
        @navigate-next="navigateToNext"
        @navigate-previous="navigateToPrevious" />
    </div>

    <!-- 2) Footer -->
    <ChargerResultsFooter activeTab="map" />

    <!-- Modal de Filtros -->
    <ChargerListItemFilter v-if="showFilters" :activeFilters="activeFilters" @close="showFilters = false" @apply="handleApplyFilters" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, nextTick, watch, onActivated } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from 'vue-i18n';
import ChargerListItem from "@/components/charger/ChargerListItem.vue";
import ChargerResultsFooter from "@/components/share/ChargerResultsFooter.vue";
import ChargerListItemFilter from "@/components/charger/ChargerListItemFilter.vue";
import { LMap, LTileLayer, LMarker, LPopup } from '@vue-leaflet/vue-leaflet';
import type { LatLngExpression } from 'leaflet';
import L from 'leaflet';
import { useApi } from "@api/integration"

const router = useRouter();
const route = useRoute();
const api = useApi();
const { t } = useI18n();

// Configuración del mapa
const zoomMapa = 14;
const latitudMadrid = 40.41694;
const longitudMadrid = -3.70361;
const lmap = ref<typeof LMap>();
const markerGroup = ref<L.LayerGroup | null>(null);

// Límites de zoom
const minZoom = 10;  // Alejamiento máximo permitido (número menor = más alejado)
const maxZoom = 18; // Acercamiento máximo permitido (número mayor = más cerca)

// Límites geográficos para España, Baleares e Islas Canarias (con margen adicional)
// [sur, oeste], [norte, este]
const spainBounds: L.LatLngBoundsExpression = [
  [26.5, -19.0],  // Sur-Oeste (sur de Canarias con margen)
  [45.0, 6.0]      // Norte-Este (norte de España, este de Baleares con margen)
];

// Estado del modal
const selectedPoint = ref<string | null>(null);

// Datos de búsqueda desde localStorage
interface SearchData {
  vehicle: string;
  IdCocheElectrico: string;
  IdTarifaNombre: number | null;
  batteryCurrent: number;
  batteryTarget: number;
  BateriaInicial: number;
  BateriaFinal: number;
  location: { lat: number; lon: number } | null;
}

const searchData = ref<SearchData | null>(null);

// Tipo para los cargadores
type ChargerItem = {
  id: string;
  name: string;
  distanceKm: number;
  priceKwh: number;
  totalCost: number;
  powerKw: number;
  chargeTimeMin: number;
  latitud: number;
  longitud: number;
  libre: number;
  total: number;
};

const chargers = ref<ChargerItem[]>([]);

// IDs de cargadores ya pintados en el mapa (para evitar duplicados)
const paintedChargerIds = ref<Set<string>>(new Set());

// Variable para controlar si estamos en modo de actualización incremental (true) o reemplazo completo (false)
const isIncrementalUpdate = ref(false);

// Modal de filtros
const showFilters = ref(false);

// Filtros activos
interface ActiveFilters {
  chargerStatus: string;
  priceMax: number;
  distance: number;
  chargingTime: string;
}

const activeFilters = ref<ActiveFilters | null>(null);

// Ubicación del usuario
const userLocation = ref<{ lat: number; lon: number } | null>(null);
const locationError = ref<string | null>(null);

// Radio diagonal del mapa visible (en metros)
const mapVisibleRadius = ref<number>(10000); // Valor por defecto: 10 km

// Centro anterior del mapa (para detectar cambios)
const previousMapCenter = ref<{ lat: number; lng: number } | null>(null);

// Icono personalizado para la ubicación del usuario (azul)
const userLocationIcon = L.divIcon({
  className: 'user-location-marker',
  html: '<div class="user-location-pin"><i class="fa-solid fa-location-dot"></i></div>',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32]
});

// Función para crear iconos de cargador según su estado Libre
function getChargerIcon(libre: number, groupedCount: number = 1): L.DivIcon {
  const color = libre > 0 ? '#22c55e' : '#ef4444'; // Verde si hay tomas libres, rojo si no
  
  // Ajustar tamaño según si hay agrupación
  const size = groupedCount > 1 ? 28 : 20;
  const fontSize = groupedCount > 1 ? '12px' : '0px';
  const displayNumber = groupedCount > 1 ? groupedCount : '';
  
  return L.divIcon({
    className: 'charger-marker',
    html: `<div style="background-color: ${color}; width: ${size}px; height: ${size}px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: ${fontSize}; line-height: 1;">${displayNumber}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
}

// Centro del mapa basado en la ubicación del usuario o Madrid por defecto
const mapCenter = computed<LatLngExpression>(() => {
  if (searchData.value?.location) {
    return [searchData.value.location.lat, searchData.value.location.lon];
  }
  return [latitudMadrid, longitudMadrid];
});

const selectedPointData = ref({
  name: "",
  distanceKm: 0,
  priceKwh: 0,
  totalCost: 0,
  powerKw: 0,
  chargeTimeMin: 0,
  latitud: 0,
  longitud: 0,
  libre: 0,
  total: 0
});

// Grupo de cargadores seleccionado y índice actual
const selectedChargerGroup = ref<ChargerItem[]>([]);
const currentChargerIndex = ref<number>(0);

function formatHoraCliente(d: Date) {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// Cargar datos de búsqueda y obtener resultados
async function loadSearchResults() {
  try {
    // Leer datos de búsqueda
    const storedData = localStorage.getItem('chargerSearchData');
    if (!storedData) {
      console.warn('⚠️ No hay datos de búsqueda almacenados');
      return;
    }
    
    searchData.value = JSON.parse(storedData);
    console.log('📥 Datos de búsqueda cargados:', searchData.value);

    // Si hay ubicación, buscar cargadores cercanos
    if (searchData.value?.location) {
      // Validar parámetros requeridos
      if (!searchData.value.location?.lat || !searchData.value.location?.lon) {
        throw new Error(t("components.chargerresultsmapview.ubicacion_requerida"));
      }
      
      if (!searchData.value.IdCocheElectrico) {
        throw new Error(t("components.chargerresultsmapview.vehiculo_requerido"));
      }
      
      // Si es actualización incremental, usar el centro del mapa visible
      let searchLat = searchData.value.location.lat;
      let searchLon = searchData.value.location.lon;
      
      if (isIncrementalUpdate.value) {
        const map = lmap.value?.leafletObject;
        if (map) {
          const mapCenter = map.getCenter();
          searchLat = mapCenter.lat;
          searchLon = mapCenter.lng;
          console.log(`🔄 Usando centro del mapa para búsqueda incremental: ${searchLat}, ${searchLon}`);
        }
      }
      
      // Buscar cargadores con filtro por ubicación
      // Nota: Los nombres de parámetros deben coincidir exactamente con el swagger (PascalCase)
      const now = new Date();
      const queryParams: Record<string, any> = {
        Latitud: searchLat,
        Longitud: searchLon,
        Take: 50,
        Skip: 0,
        OrderBy: "Distancia", // Por defecto: Distancia (según CargadorOrderByEnum)
        BateriaInicial: searchData.value.BateriaInicial ?? searchData.value.batteryCurrent,
        BateriaFinal: searchData.value.BateriaFinal ?? searchData.value.batteryTarget,
        IdCocheElectrico: searchData.value.IdCocheElectrico,
        HoraCliente: formatHoraCliente(now),
      };
      
      // Solo agregar IdTarifaNombre si tiene un valor válido (no null ni undefined)
      if (searchData.value.IdTarifaNombre != null) {
        queryParams.IdTarifaNombre = searchData.value.IdTarifaNombre;
      }
      
      // Aplicar filtros si están activos
      if (activeFilters.value) {
        // Precio máximo (según swagger: PrecioHasta)
        if (activeFilters.value.priceMax > 0) {
          queryParams.PrecioHasta = activeFilters.value.priceMax;
        }
        
        // Estado del cargador: SoloLibres = true si está seleccionado "Libre"
        // Si selecciona "Todos" no hacemos nada (no pasamos el parámetro)
        if (activeFilters.value.chargerStatus === 'libre') {
          queryParams.SoloLibres = true;
        }
        
        // Tiempo de carga máximo (según swagger: TiempoMaximo, en minutos)
        if (activeFilters.value.chargingTime !== 'any') {
          const timeMap: Record<string, number> = {
            '<15': 15,
            '<30': 30,
            '<45': 45,
            '<60': 60
          };
          const maxTimeMinutes = timeMap[activeFilters.value.chargingTime];
          if (maxTimeMinutes) {
            queryParams.TiempoMaximo = maxTimeMinutes;
          }
        }
        
        // NOTA: El filtro de distancia se ignora, siempre usamos el radio del mapa visible
        // Se mantiene el código comentado por referencia:
        // if (activeFilters.value.distance > 0) {
        //   queryParams.RadioMetros = activeFilters.value.distance * 1000; // km a metros
        // }
      }
      
      // Siempre usar el radio diagonal del mapa visible, independientemente de los filtros
      queryParams.RadioMetros = Math.round(mapVisibleRadius.value);
      console.log(`📐 Usando radio del mapa visible para la búsqueda: ${Math.round(mapVisibleRadius.value)} metros`);
      
      // Filtrar parámetros null/undefined antes de enviar
      const cleanParams = Object.fromEntries(
        Object.entries(queryParams).filter(([_, value]) => value != null && value !== undefined && value !== '')
      );
      
      console.log('📤 Parámetros de búsqueda:', cleanParams);
      
      const cargadores = await api.callStandardApi(
        "getCargadorAll",
        { showLoading: false },
         queryParams["Take"],
        queryParams["Skip"],
        queryParams["BateriaInicial"],
        queryParams["BateriaFinal"],
        queryParams["IdCocheElectrico"],
        queryParams["IdTarifaNombre"],
        queryParams["Latitud"],
        queryParams["Longitud"],
        queryParams["OrderBy"],
        queryParams["SoloLibres"],
        queryParams["HoraCliente"],
        queryParams["PrecioHasta"],
        queryParams["RadioMetros"],
        queryParams["TiempoMaximo"]
      );
      
      console.log('✅ Cargadores obtenidos:', cargadores);
      
      // Transformar datos de la API al formato esperado
      let nuevosCargadores = cargadores
        .filter((item: any) => {
          // Validar que las coordenadas existen y son válidas
          const lat = Number(item.Latitud);
          const lon = Number(item.Longitud);
          return (
            item.Latitud != null && 
            item.Longitud != null &&
            !isNaN(lat) && 
            !isNaN(lon) &&
            lat >= -90 && lat <= 90 &&
            lon >= -180 && lon <= 180
          );
        })
        .map((item: any) => {
          // Convertir coordenadas a números para asegurar que sean válidas
          const lat = Number(item.Latitud);
          const lon = Number(item.Longitud);
          
          return {
            id: item.Id,
            name: item.Direccion,
            distanceKm: item.DistanciaM ? Math.round(item.DistanciaM / 1000 * 10) / 10 : 0,
            priceKwh: item.PrecioKwh || 0,
            totalCost: item.CosteTotal || 0,
            powerKw: item.Potencia || 0,
            chargeTimeMin: item.TiempoCarga || calculateChargeTime(item.Potencia, searchData.value?.batteryCurrent || 0, searchData.value?.batteryTarget || 0),
            latitud: lat, // Ya convertido a número
            longitud: lon, // Ya convertido a número
            libre: item.Libre ?? 0,
            total: item.Total ?? 0
          };
        });
      
      // Aplicar filtros del lado del cliente (para filtros que no soporta la API o como respaldo)
      if (activeFilters.value) {
        nuevosCargadores = nuevosCargadores.filter((item: ChargerItem) => {
          // Filtro por precio máximo
          if (activeFilters.value!.priceMax > 0 && item.priceKwh > activeFilters.value!.priceMax) {
            return false;
          }
          
          // Filtro por distancia (ya debería venir filtrado del servidor, pero lo verificamos)
          if (activeFilters.value!.distance > 0 && item.distanceKm > activeFilters.value!.distance) {
            return false;
          }
          
          // Filtro por tiempo de carga
          if (activeFilters.value!.chargingTime !== 'any') {
            const timeMap: Record<string, number> = {
              '<15': 15,
              '<30': 30,
              '<45': 45,
              '<60': 60
            };
            const maxTimeMinutes = timeMap[activeFilters.value!.chargingTime];
            if (maxTimeMinutes && item.chargeTimeMin > maxTimeMinutes) {
              return false;
            }
          }
          
          // Filtro por estado del cargador
          if (activeFilters.value!.chargerStatus === 'libre' && item.libre === 0) {
            return false;
          }
          
          return true;
        });
      }
      
      // Si es actualización incremental, fusionar con los existentes (solo nuevos)
      if (isIncrementalUpdate.value) {
        const existingIds = new Set(chargers.value.map((c: ChargerItem) => c.id));
        const newChargers = nuevosCargadores.filter((c: ChargerItem) => !existingIds.has(c.id));
        
        if (newChargers.length > 0) {
          chargers.value = [...chargers.value, ...newChargers];
          console.log(`📊 Agregados ${newChargers.length} cargadores nuevos. Total: ${chargers.value.length}`);
        } else {
          console.log('📊 No hay cargadores nuevos para agregar');
        }
      } else {
        // Reemplazo completo (primera carga o aplicación de filtros)
        chargers.value = nuevosCargadores;
        paintedChargerIds.value.clear(); // Limpiar IDs pintados
        console.log('📊 Resultados procesados:', chargers.value);
      }
      
      // Centrar el mapa en la ubicación del usuario solo si no es actualización incremental
      if (!isIncrementalUpdate.value && chargers.value.length > 0 && searchData.value.location) {
        const map = lmap.value?.leafletObject;
        if (map) {
          map.setView([searchData.value.location.lat, searchData.value.location.lon], zoomMapa);
        }
      }
      
      // Actualizar los marcadores en el mapa
      await nextTick();
      createOrUpdateMarkers();
      
    } else {
      console.warn('⚠️ No hay ubicación disponible para buscar cargadores');
    }
    
  } catch (error: any) {
    console.error('❌ Error cargando resultados:', error);
    
    // Mensajes de error más específicos
    if (error?.response?.status === 400) {
      const errorMessage = error?.response?.data?.message || error?.response?.data || error?.message || 'Error en la solicitud';
      console.error('🔴 Detalles del error 400:', {
        url: error?.config?.url,
        params: error?.config?.params,
        response: error?.response?.data
      });
    } else if (error?.response?.status === 401) {
      console.error('🔴 Error 401: No autorizado');
    } else if (error?.response?.status === 500) {
      console.error('🔴 Error 500: Error del servidor');
    }
    
    // El error se maneja silenciosamente en la vista de mapa, 
    // pero lo logueamos para depuración
  }
}

// Calcular tiempo de carga basado en la potencia del cargador
function calculateChargeTime(powerKw: number, currentPercent: number, targetPercent: number): number {
  if (!powerKw) return 0;
  
  const batteryCapacity = 50; // Asumir una batería de 50kWh promedio
  const energyNeeded = (targetPercent - currentPercent) / 100 * batteryCapacity;
  
  return Math.round((energyNeeded / powerKw) * 60);
}

// Calcular distancia en kilómetros entre dos puntos (ubicación original del usuario y punto del cargador)
function calculateDistanceKm(chargerLat: number, chargerLon: number): number {
  // Obtener la ubicación original del usuario desde searchData
  if (!searchData.value?.location) {
    console.warn('⚠️ No hay ubicación original del usuario para calcular distancia');
    return 0;
  }
  
  const userLocation = searchData.value.location;
  const userLatLng = L.latLng(userLocation.lat, userLocation.lon);
  const chargerLatLng = L.latLng(chargerLat, chargerLon);
  
  // Calcular distancia en metros usando Leaflet
  const distanceMeters = userLatLng.distanceTo(chargerLatLng);
  
  // Convertir a kilómetros y redondear a 1 decimal
  const distanceKm = Math.round((distanceMeters / 1000) * 10) / 10;
  
  return distanceKm;
}

// Tipo para grupos de cargadores
type ChargerGroup = {
  chargers: ChargerItem[];
  centerLat: number;
  centerLng: number;
  hasLibre: boolean; // true si alguno tiene libre > 0
  count: number;
};

// Función para agrupar cargadores cercanos (dentro de 10 metros)
function groupNearbyChargers(chargersList: ChargerItem[], radiusMeters: number = 10): ChargerGroup[] {
  const groups: ChargerGroup[] = [];
  const processed = new Set<string>();
  
  for (const charger of chargersList) {
    // Si ya está procesado, saltarlo
    if (processed.has(charger.id)) {
      continue;
    }
    
    // Validar coordenadas
    const lat = Number(charger.latitud);
    const lon = Number(charger.longitud);
    if (
      charger.latitud == null || 
      charger.longitud == null ||
      isNaN(lat) || 
      isNaN(lon) ||
      lat < -90 || lat > 90 ||
      lon < -180 || lon > 180
    ) {
      continue;
    }
    
    const chargerLatLng = L.latLng(lat, lon);
    const group: ChargerItem[] = [charger];
    processed.add(charger.id);
    
    // Buscar otros cargadores dentro del radio
    for (const otherCharger of chargersList) {
      if (processed.has(otherCharger.id)) {
        continue;
      }
      
      const otherLat = Number(otherCharger.latitud);
      const otherLon = Number(otherCharger.longitud);
      if (
        otherCharger.latitud == null || 
        otherCharger.longitud == null ||
        isNaN(otherLat) || 
        isNaN(otherLon) ||
        otherLat < -90 || otherLat > 90 ||
        otherLon < -180 || otherLon > 180
      ) {
        continue;
      }
      
      const otherLatLng = L.latLng(otherLat, otherLon);
      const distance = chargerLatLng.distanceTo(otherLatLng);
      
      if (distance <= radiusMeters) {
        group.push(otherCharger);
        processed.add(otherCharger.id);
      }
    }
    
    // Calcular el centro del grupo (promedio de coordenadas)
    const avgLat = group.reduce((sum, c) => sum + Number(c.latitud), 0) / group.length;
    const avgLng = group.reduce((sum, c) => sum + Number(c.longitud), 0) / group.length;
    
    // Determinar si hay algún cargador libre
    const hasLibre = group.some(c => c.libre > 0);
    
    groups.push({
      chargers: group,
      centerLat: avgLat,
      centerLng: avgLng,
      hasLibre: hasLibre,
      count: group.length
    });
  }
  
  return groups;
}

// Crear o reiniciar los marcadores en el mapa
function createOrUpdateMarkers() {
  const map = lmap.value?.leafletObject;
  if (!map) {
    console.warn('⚠️ El mapa no está disponible aún');
    return;
  }

  // Verificar que hay cargadores antes de crear los marcadores
  if (!chargers.value || chargers.value.length === 0) {
    console.warn('⚠️ No hay cargadores para mostrar en el mapa');
    return;
  }

  // Si no existe un grupo de marcadores, crearlo
  if (!markerGroup.value) {
    markerGroup.value = L.layerGroup();
    markerGroup.value.addTo(map as any);
  }

  // Limpiar marcadores anteriores si es actualización completa
  if (!isIncrementalUpdate.value && markerGroup.value) {
    // Eliminar todas las capas del grupo
    markerGroup.value.eachLayer((layer: any) => {
      markerGroup.value?.removeLayer(layer);
    });
    paintedChargerIds.value.clear();
  }

  const group = markerGroup.value;
  let newMarkersCount = 0;
  
  // Para actualizaciones completas, reagrupar todos los cargadores
  // Para actualizaciones incrementales, solo procesar los nuevos
  const chargersToProcess = isIncrementalUpdate.value 
    ? chargers.value.filter(charger => !paintedChargerIds.value.has(charger.id))
    : chargers.value;
  
  if (chargersToProcess.length === 0) {
    console.log('📊 No hay cargadores para procesar');
    return;
  }
  
  // Agrupar cargadores cercanos (dentro de 10 metros)
  const chargerGroups = groupNearbyChargers(chargersToProcess, 10);
  
  console.log(`📊 Agrupados ${chargersToProcess.length} cargadores en ${chargerGroups.length} grupos`);
  
  // Crear marcadores para cada grupo
  chargerGroups.forEach(chargerGroup => {
    try {
      // Validar coordenadas del centro
      const lat = chargerGroup.centerLat;
      const lng = chargerGroup.centerLng;
      
      if (
        isNaN(lat) || 
        isNaN(lng) ||
        lat < -90 || lat > 90 ||
        lng < -180 || lng > 180
      ) {
        console.warn(`⚠️ Grupo tiene coordenadas inválidas: lat=${lat}, lng=${lng}`);
        return;
      }

      // Crear objeto LatLng explícitamente para asegurar que sea válido
      const latLng = L.latLng(lat, lng);
      
      // Verificar que las coordenadas se crearon correctamente
      if (!latLng || isNaN(latLng.lat) || isNaN(latLng.lng)) {
        console.warn(`⚠️ No se pudieron crear coordenadas válidas para el grupo`);
        return;
      }

      // Determinar el color: verde si alguno está libre, rojo si todos están ocupados
      const libreValue = chargerGroup.hasLibre ? 1 : 0;
      
      // Crear el marcador con el objeto LatLng explícito
      const marker = L.marker(latLng, {
        icon: getChargerIcon(libreValue, chargerGroup.count)
      });
      
      // Verificar que el marcador tiene coordenadas válidas
      const markerLatLng = marker.getLatLng();
      if (!markerLatLng || isNaN(markerLatLng.lat) || isNaN(markerLatLng.lng)) {
        console.warn(`⚠️ Marcador creado sin coordenadas válidas para el grupo`);
        return;
      }
      
      // Almacenar los IDs de los cargadores del grupo en los datos del marcador
      (marker as any).chargerIds = chargerGroup.chargers.map(c => c.id);
      // Almacenar también el grupo completo de cargadores en el marcador
      (marker as any).chargerGroup = chargerGroup.chargers;
      
      // Manejar clic en el marcador - mostrar el primer cargador del grupo
      marker.on('click', () => {
        // Si hay múltiples cargadores, mostrar el primero
        const firstChargerId = chargerGroup.chargers[0].id;
        selectPoint(firstChargerId, chargerGroup.chargers);
      });
      
      group.addLayer(marker);
      
      // Marcar todos los cargadores del grupo como pintados
      chargerGroup.chargers.forEach(charger => {
        paintedChargerIds.value.add(charger.id);
      });
      
      newMarkersCount++;
    } catch (error) {
      console.error(`❌ Error creando marcador para grupo:`, error);
    }
  });

  // Si se agregaron nuevos marcadores, loguear
  if (newMarkersCount > 0) {
    console.log(`✅ Marcadores agregados: ${newMarkersCount} nuevos (agrupados). Total pintados: ${paintedChargerIds.value.size}`);
  }
}

function selectPoint(pointId: string, chargerGroup?: ChargerItem[]) {
  console.log('Marcador clickeado:', pointId);
  selectedPoint.value = pointId;
  
  // Si se proporciona un grupo, almacenarlo
  if (chargerGroup && chargerGroup.length > 0) {
    selectedChargerGroup.value = chargerGroup;
    // Encontrar el índice del cargador seleccionado en el grupo
    const index = chargerGroup.findIndex(c => c.id === pointId);
    currentChargerIndex.value = index >= 0 ? index : 0;
  } else {
    // Si no hay grupo, buscar el cargador individual
    const charger = chargers.value.find(c => c.id === pointId);
    if (charger) {
      selectedChargerGroup.value = [charger];
      currentChargerIndex.value = 0;
    }
  }
  
  // Actualizar los datos del punto seleccionado
  const charger = chargers.value.find(c => c.id === pointId);
  if (charger) {
    // Calcular la distancia desde la ubicación original del usuario al punto seleccionado
    const calculatedDistanceKm = calculateDistanceKm(charger.latitud, charger.longitud);
    
    selectedPointData.value = {
      name: charger.name,
      distanceKm: calculatedDistanceKm, // Usar la distancia calculada desde la ubicación original
      priceKwh: charger.priceKwh,
      totalCost: charger.totalCost,
      powerKw: charger.powerKw,
      chargeTimeMin: charger.chargeTimeMin,
      latitud: charger.latitud,
      longitud: charger.longitud,
      libre: charger.libre,
      total: charger.total
    };
  }
}

// Función para navegar al siguiente cargador del grupo
function navigateToNext() {
  if (selectedChargerGroup.value.length === 0) return;
  
  currentChargerIndex.value = (currentChargerIndex.value + 1) % selectedChargerGroup.value.length;
  const nextCharger = selectedChargerGroup.value[currentChargerIndex.value];
  if (nextCharger) {
    selectPoint(nextCharger.id, selectedChargerGroup.value);
  }
}

// Función para navegar al cargador anterior del grupo
function navigateToPrevious() {
  if (selectedChargerGroup.value.length === 0) return;
  
  currentChargerIndex.value = currentChargerIndex.value === 0 
    ? selectedChargerGroup.value.length - 1 
    : currentChargerIndex.value - 1;
  const prevCharger = selectedChargerGroup.value[currentChargerIndex.value];
  if (prevCharger) {
    selectPoint(prevCharger.id, selectedChargerGroup.value);
  }
}

function closeModal() {
  selectedPoint.value = null;
  selectedChargerGroup.value = [];
  currentChargerIndex.value = 0;
}

function centerOnUserLocation() {
  if (!userLocation.value) return;
  
  const map = lmap.value?.leafletObject;
  if (map) {
    const currentZoom = map.getZoom();
    map.flyTo([userLocation.value.lat, userLocation.value.lon], currentZoom, {
      duration: 0.5
    });
  }
}


// Cargar filtros desde localStorage
function loadFiltersFromStorage() {
  try {
    const storedFilters = localStorage.getItem('chargerActiveFilters');
    if (storedFilters) {
      activeFilters.value = JSON.parse(storedFilters);
      console.log('📥 Filtros cargados desde localStorage:', activeFilters.value);
    }
  } catch (error) {
    console.warn('⚠️ Error cargando filtros desde localStorage:', error);
  }
}

// Guardar filtros en localStorage
function saveFiltersToStorage(filters: ActiveFilters) {
  try {
    localStorage.setItem('chargerActiveFilters', JSON.stringify(filters));
    console.log('💾 Filtros guardados en localStorage:', filters);
  } catch (error) {
    console.warn('⚠️ Error guardando filtros en localStorage:', error);
  }
}

// Manejar la aplicación de filtros
function handleApplyFilters(filters: ActiveFilters) {
  activeFilters.value = filters;
  // Guardar filtros en localStorage para compartir entre vistas
  saveFiltersToStorage(filters);
  // Recargar resultados con los nuevos filtros (reemplazo completo)
  isIncrementalUpdate.value = false;
  loadSearchResults();
}

// Función de debounce para llamadas a la API cuando cambia el radio o el centro
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
function debouncedLoadSearchResults(reason: string = '') {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(async () => {
    console.log(`🔄 ${reason}, recargando cargadores...`);
    isIncrementalUpdate.value = true; // Modo incremental
    await loadSearchResults();
  }, 500); // Esperar 500ms después del último cambio
}

// Calcular y mostrar la distancia desde la ubicación del usuario hasta el centro del mapa
function calculateDistanceToMapCenter() {
  console.log('🔍 calculateDistanceToMapCenter llamada');
  const map = lmap.value?.leafletObject;
  if (!map) {
    console.warn('⚠️ El mapa no está disponible');
    return;
  }

  try {
    // Obtener el centro del mapa visible
    const mapCenter = map.getCenter();
    console.log('🗺️ Centro del mapa:', mapCenter);
    
    // Calcular el radio visible del mapa (desde el centro hasta el borde superior)
    const bounds = map.getBounds();
    const northEdge = bounds.getNorth();
    const southEdge = bounds.getSouth();
    const eastEdge = bounds.getEast();
    const westEdge = bounds.getWest();
    
    // Calcular distancia desde el centro hasta el borde norte (vertical)
    const centerToNorth = L.latLng(mapCenter.lat, mapCenter.lng);
    const northEdgePoint = L.latLng(northEdge, mapCenter.lng); // Misma longitud, borde norte
    const radiusNorthMeters = centerToNorth.distanceTo(northEdgePoint);
    
    // Calcular distancia desde el centro hasta el borde este (horizontal)
    const eastEdgePoint = L.latLng(mapCenter.lat, eastEdge); // Misma latitud, borde este
    const radiusEastMeters = centerToNorth.distanceTo(eastEdgePoint);
    
    // El radio más grande será el que determine el área visible completa
    const maxRadiusMeters = Math.max(radiusNorthMeters, radiusEastMeters);
    
    // Actualizar el radio visible del mapa (para usar en la API)
    const previousRadius = mapVisibleRadius.value;
    mapVisibleRadius.value = maxRadiusMeters;
    
    console.log(`📏 Radio visible del mapa:`);
    console.log(`   - Hacia el norte: ${Math.round(radiusNorthMeters)} metros`);
    console.log(`   - Hacia el este: ${Math.round(radiusEastMeters)} metros`);
    console.log(`   - Radio máximo (diagonal): ${Math.round(maxRadiusMeters)} metros`);
    
    // Detectar si el radio cambió significativamente (más del 10%)
    const radiusChange = Math.abs(previousRadius - maxRadiusMeters);
    const radiusChangePercent = previousRadius > 0 ? (radiusChange / previousRadius) * 100 : 100;
    let shouldReload = radiusChangePercent > 10;
    let reloadReason = '';
    
    if (shouldReload) {
      reloadReason = `📐 Radio cambió ${radiusChangePercent.toFixed(1)}%`;
    }
    
    // Detectar si el centro del mapa cambió significativamente
    if (previousMapCenter.value) {
      const previousCenter = L.latLng(previousMapCenter.value.lat, previousMapCenter.value.lng);
      const currentCenter = L.latLng(mapCenter.lat, mapCenter.lng);
      const centerDistanceMeters = previousCenter.distanceTo(currentCenter);
      
      // Si el centro se movió más del 20% del radio actual, recargar cargadores
      const centerChangeThreshold = maxRadiusMeters * 0.2; // 20% del radio
      
      if (centerDistanceMeters > centerChangeThreshold) {
        shouldReload = true;
        reloadReason = reloadReason 
          ? `${reloadReason} y centro se movió ${Math.round(centerDistanceMeters)}m` 
          : `📍 Centro del mapa se movió ${Math.round(centerDistanceMeters)}m`;
        console.log(`📍 Centro del mapa cambió: ${Math.round(centerDistanceMeters)}m (umbral: ${Math.round(centerChangeThreshold)}m)`);
      }
    }
    
    // Actualizar el centro anterior
    previousMapCenter.value = {
      lat: mapCenter.lat,
      lng: mapCenter.lng
    };
    
    // Recargar cargadores si hubo cambios significativos
    if (shouldReload) {
      console.log(`${reloadReason}, se recargarán cargadores`);
      debouncedLoadSearchResults(reloadReason);
    }
    
    // Usar la ubicación del usuario o Madrid por defecto para depuración
    const referenceLocation = userLocation.value || {
      lat: latitudMadrid,
      lon: longitudMadrid
    };
    console.log('📍 Ubicación de referencia:', referenceLocation);
    
    // Crear objetos LatLng para calcular la distancia
    const userLatLng = L.latLng(referenceLocation.lat, referenceLocation.lon);
    const centerLatLng = L.latLng(mapCenter.lat, mapCenter.lng);
    
    // Calcular la distancia en metros usando el método distanceTo de Leaflet
    const distanceInMeters = userLatLng.distanceTo(centerLatLng);
    
    // Mostrar en consola
    const locationLabel = userLocation.value ? 'tu ubicación' : 'ubicación por defecto (Madrid)';
    console.log(`📍 Distancia desde ${locationLabel} hasta el centro del mapa: ${Math.round(distanceInMeters)} metros`);
  } catch (error) {
    console.warn('⚠️ Error calculando la distancia:', error);
  }
}

// Obtener la ubicación del usuario
function getUserLocation() {
  if (!navigator.geolocation) {
    locationError.value = t("components.chargerresultsmapview.navegador_no_soporta_geolocalizacion");
    return;
  }

  let errorHandled = false;
  const timeoutDuration = 12000; // 12 segundos para dar tiempo al timeout del geolocation
  
  // Timeout de seguridad adicional para detectar problemas silenciosos
  const safetyTimeout = setTimeout(() => {
    if (!errorHandled) {
      console.warn('⚠️ Timeout de seguridad: la geolocalización no respondió');
      errorHandled = true;
      locationError.value = t("components.chargerresultsmapview.tiempo_espera_agotado");
    }
  }, timeoutDuration);

  try {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!errorHandled) {
          clearTimeout(safetyTimeout);
          errorHandled = true;
          userLocation.value = {
            lat: position.coords.latitude,
            lon: position.coords.longitude
          };
          locationError.value = null;
          
          // Centrar el mapa en la ubicación del usuario si no hay cargadores aún
          const map = lmap.value?.leafletObject;
          if (map && (!searchData.value?.location || chargers.value.length === 0)) {
            map.setView([userLocation.value.lat, userLocation.value.lon], zoomMapa);
          }
          
          console.log('✅ Ubicación del usuario obtenida:', userLocation.value);
        }
      },
      (error) => {
        if (!errorHandled) {
          clearTimeout(safetyTimeout);
          errorHandled = true;
          console.warn('⚠️ Error obteniendo ubicación:', error);
          
          let errorMessage = t("components.chargerresultsmapview.no_pudo_acceder_ubicacion");
          
          if (error?.code !== undefined) {
            switch (error.code) {
              case 1: // PERMISSION_DENIED
                errorMessage = t("components.chargerresultsmapview.permiso_ubicacion_denegado");
                break;
              case 2: // POSITION_UNAVAILABLE
                errorMessage = t("components.chargerresultsmapview.ubicacion_no_disponible");
                break;
              case 3: // TIMEOUT
                errorMessage = t("components.chargerresultsmapview.tiempo_espera_agotado");
                break;
              default:
                errorMessage = t("components.chargerresultsmapview.no_pudo_acceder_ubicacion");
                break;
            }
          } else if (error?.message?.toLowerCase().includes('permission') || 
                     error?.message?.toLowerCase().includes('policy') ||
                     error?.message?.toLowerCase().includes('geolocation')) {
            // Detectar violaciones de política de permisos
            errorMessage = t("components.chargerresultsmapview.geolocalizacion_bloqueada");
          }
          
          // SIEMPRE establecer el mensaje de error cuando falle
          locationError.value = errorMessage;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  } catch (policyError: any) {
    // Capturar errores de política que pueden ocurrir antes del callback
    if (!errorHandled) {
      clearTimeout(safetyTimeout);
      errorHandled = true;
      console.warn('⚠️ Violación de política de permisos detectada:', policyError);
      locationError.value = t("components.chargerresultsmapview.geolocalizacion_bloqueada");
    }
  }
}

// Parchear métodos de Leaflet para prevenir errores con _leaflet_events undefined
function patchLeafletEventHandling() {
  if (!L) return;

  // Parchear el método off de Evented para manejar _leaflet_events undefined
  if ((L as any).Evented && (L as any).Evented.prototype) {
    const originalOff = (L as any).Evented.prototype.off;
    if (originalOff && !(originalOff as any)._patched) {
      (L as any).Evented.prototype.off = function(type?: string, fn?: Function, context?: any) {
        try {
          // Validar que _leaflet_events existe antes de acceder
          if (!this || !this._leaflet_events) {
            return this;
          }
          return originalOff.call(this, type, fn, context);
        } catch (error) {
          // Si hay error, simplemente retornar this para no romper la cadena
          return this;
        }
      };
      (L as any).Evented.prototype.off._patched = true;
    }

    // Parchear también el método onRemove de Marker que puede causar problemas
    if ((L as any).Marker && (L as any).Marker.prototype) {
      const originalOnRemove = (L as any).Marker.prototype.onRemove;
      if (originalOnRemove && !(originalOnRemove as any)._patched) {
        (L as any).Marker.prototype.onRemove = function(map: any) {
          try {
            // Validar que el objeto tiene _leaflet_events antes de proceder
            if (!this || !this._leaflet_events) {
              return this;
            }
            return originalOnRemove.call(this, map);
          } catch (error) {
            // Si hay error, simplemente retornar this
            return this;
          }
        };
        (L as any).Marker.prototype.onRemove._patched = true;
      }
    }

    // Parchear removeLayer de LayerGroup para manejar mejor la eliminación
    if ((L as any).LayerGroup && (L as any).LayerGroup.prototype) {
      const originalRemoveLayer = (L as any).LayerGroup.prototype.removeLayer;
      if (originalRemoveLayer && !(originalRemoveLayer as any)._patched) {
        (L as any).LayerGroup.prototype.removeLayer = function(layer: any) {
          try {
            // Validar que layer existe y es válido
            if (!layer) {
              return this;
            }
            // Validar que layer tiene _leaflet_events antes de intentar removerlo
            if (layer._leaflet_events === undefined && layer._leaflet_id === undefined) {
              // Si el layer ya fue eliminado, simplemente retornar
              return this;
            }
            return originalRemoveLayer.call(this, layer);
          } catch (error) {
            // Si hay error, simplemente retornar this
            return this;
          }
        };
        (L as any).LayerGroup.prototype.removeLayer._patched = true;
      }
    }
  }
}

// Parchear LatLngBounds.intersects y Bounds.intersects una vez al inicio para prevenir errores
function patchLeafletIntersects() {
  if (!L) return;

  // Parchear LatLngBounds.intersects
  if ((L as any).LatLngBounds) {
    const originalLatLngIntersects = (L as any).LatLngBounds.prototype.intersects;
    if (originalLatLngIntersects && !(originalLatLngIntersects as any)._patched) {
      (L as any).LatLngBounds.prototype.intersects = function(other: any) {
        try {
          // Validar que 'this' tiene bounds válidos
          if (this._southWest) {
            const sw = this._southWest;
            if (!sw || sw.lat === undefined || sw.lat === null || isNaN(sw.lat) || 
                sw.lng === undefined || sw.lng === null || isNaN(sw.lng)) {
              return false;
            }
          }
          if (this._northEast) {
            const ne = this._northEast;
            if (!ne || ne.lat === undefined || ne.lat === null || isNaN(ne.lat) || 
                ne.lng === undefined || ne.lng === null || isNaN(ne.lng)) {
              return false;
            }
          }

          // Validar que 'other' existe
          if (!other) {
            return false;
          }

          // Si other es un LatLngBounds, validar sus propiedades
          if (other._southWest || other._northEast) {
            const sw = other._southWest;
            const ne = other._northEast;
            if (sw && (sw.lat === undefined || sw.lat === null || isNaN(sw.lat) || 
                      sw.lng === undefined || sw.lng === null || isNaN(sw.lng))) {
              return false;
            }
            if (ne && (ne.lat === undefined || ne.lat === null || isNaN(ne.lat) || 
                      ne.lng === undefined || ne.lng === null || isNaN(ne.lng))) {
              return false;
            }
          }

          // Si other tiene getBounds, obtener y validar sus bounds
          if (other && typeof other.getBounds === 'function') {
            try {
              const bounds = other.getBounds();
              if (!bounds) {
                return false;
              }
              if (bounds._southWest || bounds._northEast) {
                const sw = bounds._southWest;
                const ne = bounds._northEast;
                if (sw && (sw.lat === undefined || sw.lat === null || isNaN(sw.lat) || 
                          sw.lng === undefined || sw.lng === null || isNaN(sw.lng))) {
                  return false;
                }
                if (ne && (ne.lat === undefined || ne.lat === null || isNaN(ne.lat) || 
                          ne.lng === undefined || ne.lng === null || isNaN(ne.lng))) {
                  return false;
                }
              }
            } catch (e) {
              return false;
            }
          }

          // Llamar al método original
          return originalLatLngIntersects.call(this, other);
        } catch (error) {
          // Silenciar errores para no romper la aplicación
          return false;
        }
      };
      (L as any).LatLngBounds.prototype.intersects._patched = true;
    }
  }

  // Parchear Bounds.intersects (usado en spiderfy y otras operaciones de pantalla)
  if ((L as any).Bounds) {
    const originalBoundsIntersects = (L as any).Bounds.prototype.intersects;
    if (originalBoundsIntersects && !(originalBoundsIntersects as any)._patched) {
      (L as any).Bounds.prototype.intersects = function(other: any) {
        try {
          // Validar que 'this' tiene propiedades válidas
          if (this.min) {
            const min = this.min;
            if (!min || min.x === undefined || min.x === null || isNaN(min.x) || 
                min.y === undefined || min.y === null || isNaN(min.y)) {
              return false;
            }
          }
          if (this.max) {
            const max = this.max;
            if (!max || max.x === undefined || max.x === null || isNaN(max.x) || 
                max.y === undefined || max.y === null || isNaN(max.y)) {
              return false;
            }
          }

          // Validar que 'other' existe
          if (!other) {
            return false;
          }

          // Si other es un Bounds, validar sus propiedades
          if (other.min || other.max) {
            const min = other.min;
            const max = other.max;
            if (min && (min.x === undefined || min.x === null || isNaN(min.x) || 
                       min.y === undefined || min.y === null || isNaN(min.y))) {
              return false;
            }
            if (max && (max.x === undefined || max.x === null || isNaN(max.x) || 
                       max.y === undefined || max.y === null || isNaN(max.y))) {
              return false;
            }
          }

          // Llamar al método original
          return originalBoundsIntersects.call(this, other);
        } catch (error) {
          // Silenciar errores para no romper la aplicación
          return false;
        }
      };
      (L as any).Bounds.prototype.intersects._patched = true;
    }
  }
}

// Watcher para recargar filtros cuando cambia la ruta (navegación entre vistas)
watch(() => route.path, async (newPath) => {
  if (newPath === '/charger-results-map') {
    // Recargar filtros desde localStorage al entrar en la vista del mapa
    const previousFilters = activeFilters.value ? JSON.stringify(activeFilters.value) : null;
    loadFiltersFromStorage();
    const currentFilters = activeFilters.value ? JSON.stringify(activeFilters.value) : null;
    
    // Si los filtros cambiaron, recargar resultados (reemplazo completo)
    if (previousFilters !== currentFilters) {
      isIncrementalUpdate.value = false;
      await loadSearchResults();
    }
  }
});

// Hook para cuando el componente se activa (si está dentro de keep-alive)
onActivated(async () => {
  // Recargar filtros y resultados cuando se activa el componente
  loadFiltersFromStorage();
  isIncrementalUpdate.value = false; // Reemplazo completo al activar
  await loadSearchResults();
});

onMounted(async () => {
  // Aplicar los parches antes de cualquier operación del mapa
  patchLeafletEventHandling();
  patchLeafletIntersects();
  
  // Cargar filtros desde localStorage antes de cargar resultados
  loadFiltersFromStorage();
  
  // Aplicar límites del mapa después de que se inicialice
  await nextTick();
  
  // Esperar un poco más para asegurar que el mapa esté completamente inicializado
  setTimeout(() => {
    const map = lmap.value?.leafletObject;
    if (map) {
      console.log('🗺️ Mapa inicializado, agregando listeners');
      map.setMaxBounds(spainBounds);
      
      // Agregar control de ubicación personalizado
      const LocateControl = L.Control.extend({
        onAdd: function() {
          const div = L.DomUtil.create('div', 'leaflet-control-locate');
          div.innerHTML = '<button class="leaflet-locate-button" title="' + t('components.chargerresultsmapview.centrar_ubicacion') + '"><i class="fa-solid fa-location-crosshairs"></i></button>';
          L.DomEvent.disableClickPropagation(div);
          L.DomEvent.on(div, 'click', function() {
            centerOnUserLocation();
          });
          return div;
        }
      });
      new LocateControl({ position: 'bottomright' }).addTo(map);
      
      // Agregar listeners para cuando el mapa se mueva
      map.on('moveend', () => {
        console.log('📱 Evento moveend disparado');
        calculateDistanceToMapCenter();
      });
      
      map.on('dragend', () => {
        console.log('📱 Evento dragend disparado');
        calculateDistanceToMapCenter();
      });
      
      map.on('zoomend', () => {
        console.log('📱 Evento zoomend disparado');
        calculateDistanceToMapCenter();
      });
      
      // Calcular distancia inicial y establecer el centro anterior
      calculateDistanceToMapCenter();
      // Establecer el centro inicial para comparaciones futuras
      if (!previousMapCenter.value && map.getCenter()) {
        const initialCenter = map.getCenter();
        previousMapCenter.value = {
          lat: initialCenter.lat,
          lng: initialCenter.lng
        };
      }
    } else {
      console.warn('⚠️ No se pudo obtener el objeto del mapa');
    }
  }, 100);
  
  getUserLocation();
  isIncrementalUpdate.value = false; // Primera carga: reemplazo completo
  await loadSearchResults();
});
</script>

<style scoped lang="scss">
@import "@/styles/_vars.scss";
@import "@/styles/figma.scss";
@import "@/styles/_themevars.scss";

.page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--background-default-page);
}

.map-container {
  position: relative;
  flex: 1;
  width: 100%;
}

.map-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

:deep(.leaflet-control-locate) {
  margin-bottom: 100px !important;
  
  .leaflet-locate-button {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid var(--border-default-default, #C2CDDD);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: var(--text-primary-default, #D3135A);
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: all 0.2s;
    padding: 0;
    margin: 0;

    &:hover {
      background: var(--background-primary-default, #D3135A);
      color: var(--text-default-inverse, #FFF);
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }
  }
}

:deep(.leaflet-container) {
  width: 100%;
  height: 100%;
  font-family: var(--font-family-body, "Gantari");
}

:deep(h3.title) {
  text-align: left;
  padding-right: 30px;
}

:deep(.card) {
  border: 0;
}




.floating-panel {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 12px;
  max-width: 400px;
  width: calc(100% - 40px);
  max-height: 70vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateX(-50%) translateY(100%);
    opacity: 0;
  }

  to {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
}

.close-button {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  color: #6b7280;
  z-index: 10;
  transition: all 0.2s;
}

.close-button:hover {
  background: #e5e7eb;
  color: #374151;
}

// Marcador de ubicación del usuario
:deep(.user-location-marker) {
  background: transparent;
  border: none;
}

// Marcador de cargador
:deep(.charger-marker) {
  background: transparent;
  border: none;
}

.user-location-pin {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #3b82f6;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

// Mensaje de error de ubicación
.message-container {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  display: flex;
  padding: var(--card-vertical-padding, 16px) var(--card-horizontal-padding, 16px);
  align-items: center;
  gap: 10px;
  max-width: 90%;
  border-radius: var(--card-border-radius, 16px);
  background: var(--background-normal-hover, #F0F0F0);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  .location-icon {
    width: 48px;
    height: 48px;
    flex-shrink: 0;
  }

  .message-text {
    color: var(--text-default-caption, #667790);
    font-size: var(--font-size-body-sm, 14px);
    font-weight: 400;
    line-height: var(--line-height-body-sm, 18px);
    flex: 1;
  }
}

</style>
